/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import pick from 'lodash/pick';
import {error} from '@ion/core/src/logger/logger';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import DetailCardInputs from '../inputs/DetailCardInputs';
import PopupInputs from '../inputs/PopupInputs';

export type DeviceInputContentProps = {
  asset: CVSAssetType;
  popupVariant?: boolean;
};

const INPUTS = ['input1', 'input2', 'input3', 'input4', 'input5', 'input6', 'input7'];

export interface DeviceInputValue {
  label: string;
  value: string;
  active: boolean;
}

/**
 * Parses the asset/device's raw `input1`..`input7` JSON-encoded attributes
 * into the labeled/active inputs this component (and, per ION-7462, the
 * current-theme Diagnostics Inputs section) renders - extracted here so
 * both can share one parsing/filtering pass rather than each re-deriving it.
 *
 * ```typescript
 *
 *   import {getDeviceInputs} from '@ion/dls/src/components/device/DeviceInputContent';
 *   ...
 *
 *   const inputs = getDeviceInputs(asset);
 *
 * ```
 *
 * @param asset - the asset/device to read the raw input attributes from
 * @returns Every configured, labeled input, in `input1`..`input7` order
 */
export const getDeviceInputs = (asset: CVSAssetType): DeviceInputValue[] => {
  const assetInputs = pick(asset, INPUTS);
  return Object.values(assetInputs).reduce((inputs: DeviceInputValue[], val) => {
    if (!isEmpty(val)) {
      try {
        const {label, value, active} = safeJSONParse(val);
        if (!isEmpty(label) && !isEmpty(value) && (INPUTS.indexOf(label.toLowerCase()) < 0)) {
          inputs.push({label, value, active});
        }
      } catch (err) {
        error(err);
      }
    }
    return inputs;
  }, []);
};

export const useStyles = makeStyles((theme: DLSTheme) => ({
  popupContainer : {
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    margin        : '0px 0px 0px 0px'
  },
  cardValue : {
    ...dlsAlt('typography', 'label3'),
    backgroundColor : ({active}: {active: boolean}) => active ? dlsColor('caBlue') : dlsColor('white'),
    color           : ({active}: {active: boolean}) => active ? dlsColor('white') : dlsColor('caBlue'),
    textAlign       : 'center',
    boxSizing       : 'border-box',
    borderRadius    : '2px',
    border          : `2px solid ${dlsColor('caBlue')}`,
    padding         : '0px 4px',
    minWidth        : '32px'
  }
}));

/**
 * @DeviceInputContent
 *
 * This component is used in the AssetDetailsDiagnostics component to display the input status bar.
 * It is also used in the AssetDetailPopup component to display the input status bar in a more compact format.
 *
 * @param asset - The asset object.
 * @param popupVariant - The variant of the component.
 *
 * @returns A component that displays the input status for each input's label and value.
 *
 * @example
 * ```typescript
 *
 * import DeviceInputContent from '@ion/dls/src/components/device/DeviceInputContent';
 *
 * <SomeComponent>
 *   <DeviceInputContent asset={asset} />
 * </SomeComponent>
 *
 * ```
 *
 */
export const DeviceInputContent = ({
  asset,
  popupVariant = false
}: DeviceInputContentProps) => {
  const classes = useStyles();
  const assetInputs = pick(asset, INPUTS);

  return (
    <div className={popupVariant ? classes.popupContainer : null}>
      {Object.entries(assetInputs).map(([key, val], index) => {
        if (!isEmpty(val)) {
          try {
            const {label, value, active} = safeJSONParse(val);
            if (!isEmpty(label) && !isEmpty(value) && (INPUTS.indexOf(label.toLowerCase()) < 0)) {
              if (popupVariant) {
                return (
                  <PopupInputs key={index}
                    label={label}
                    value={value}
                    index={index}
                    active={active} />
                );
              }
              return (
                <DetailCardInputs key={index}
                  label={label}
                  value={value}
                  index={index}
                  active={active} />
              );
            }
          } catch (err) {
            error(err);
          }
        }
        return null;
      })}
    </div>
  );
};

export default DeviceInputContent;
